import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { useRenderOperations } from '@/features/design/render/hooks/use-render-operations';
import {
  useCreateConcept,
  useUpdateConcept,
  useDeleteAngle,
} from './use-concepts';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import type { Project } from '@/types/project';
import type { Concept } from '@/types/concept';
import type {
  AngleDeleteConflictMeta,
  DeleteConflictStrategy,
} from '@/types/project-angle';

interface UseConceptActionsProps {
  project: Project;
  concept: Concept;
  isArchived: boolean;
  onRefetch: () => void;
}

export function useConceptActions({
  project,
  concept,
  isArchived,
  onRefetch,
}: UseConceptActionsProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const setError = useConceptStore((state) => state.setError);
  const removeSelectedRenderId = useConceptStore(
    (state) => state.removeSelectedRenderId
  );

  const createConceptMutation = useCreateConcept();
  const updateConceptMutation = useUpdateConcept();
  const deleteAngleMutation = useDeleteAngle();
  const { deleteRender } = useRenderOperations();

  // Cross-concept view-delete conflict (409 ANGLE_DELETE_CONFLICT): the deleted
  // view has renders in — or is the main of — other concepts. We surface the
  // server-driven strategy dialog and retry with the chosen strategy (phase 2).
  const [deleteConflict, setDeleteConflict] =
    useState<AngleDeleteConflictMeta | null>(null);
  const [deleteConflictDetail, setDeleteConflictDetail] = useState<
    string | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<{
    conceptAngleId: string;
    angleId: string;
  } | null>(null);

  const createConceptFromAngle = useCallback(
    async (angleId: string) => {
      if (isArchived) return;

      try {
        const newConcept = await createConceptMutation.mutateAsync({
          projectId: project.id,
          mainAngleId: angleId,
        });
        // `?created=1`: one-shot flag so the destination concept header plays
        // the "new design created" pulse (ConceptEditor strips it after firing).
        router.push(
          `/project/${project.id}/concept/${newConcept.id}?created=1`
        );
      } catch (_error) {
        setError('Failed to create concept');
      }
    },
    [createConceptMutation, project.id, router, isArchived, setError]
  );

  const handleAngleUploadSuccess = useCallback(async () => {
    setError(null);
    // Only invalidate angles list - renders are handled by WebSocket and Store
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.angles(concept?.id),
    });
    await onRefetch(); // Still needed for angles and concept metadata
  }, [concept?.id, queryClient, onRefetch, setError]);

  const handleDeleteAngle = useCallback(
    async (conceptAngleId: string, angleId: string | undefined) => {
      if (!concept || !angleId) return;

      try {
        await deleteAngleMutation.mutateAsync({
          conceptId: concept.id,
          conceptAngleId,
          angleId,
        });
        // Clean up orphaned carousel selection for the deleted angle
        removeSelectedRenderId(conceptAngleId);
        queryClient.invalidateQueries({
          queryKey: cacheKeys.concepts.angles(concept.id),
        });
        await onRefetch();
      } catch (error) {
        // 409: the view is shared (renders / main elsewhere) — open the
        // strategy dialog instead of failing. The user picks how to remove it.
        if (
          error instanceof ApiClientError &&
          error.isAngleDeleteConflictError()
        ) {
          const meta = error.getAngleDeleteConflict();
          if (meta) {
            setDeleteConflict(meta);
            setDeleteConflictDetail(error.message);
            setPendingDelete({ conceptAngleId, angleId });
            return;
          }
        }
        setError('Failed to delete angle');
      }
    },
    [
      concept,
      deleteAngleMutation,
      queryClient,
      onRefetch,
      setError,
      removeSelectedRenderId,
    ]
  );

  // Phase 2: the user picked a strategy in the conflict dialog — retry the
  // delete carrying `conflict_strategy`. On success the angle may survive
  // elsewhere (keep-rendered / main-protected); we reconcile from the server.
  const handleDeleteConflictChoose = useCallback(
    async (strategy: DeleteConflictStrategy) => {
      if (!concept || !pendingDelete) return;
      const { conceptAngleId, angleId } = pendingDelete;

      try {
        await deleteAngleMutation.mutateAsync({
          conceptId: concept.id,
          conceptAngleId,
          angleId,
          strategy,
        });
        removeSelectedRenderId(conceptAngleId);
        setDeleteConflict(null);
        setDeleteConflictDetail(null);
        setPendingDelete(null);
        queryClient.invalidateQueries({
          queryKey: cacheKeys.concepts.angles(concept.id),
        });
        await onRefetch();
      } catch (_error) {
        setError('Failed to delete angle');
      }
    },
    [
      concept,
      pendingDelete,
      deleteAngleMutation,
      queryClient,
      onRefetch,
      setError,
      removeSelectedRenderId,
    ]
  );

  const dismissDeleteConflict = useCallback(() => {
    setDeleteConflict(null);
    setDeleteConflictDetail(null);
    setPendingDelete(null);
  }, []);

  const handleDeleteRender = useCallback(
    async (renderId: string) => {
      if (!concept) return;

      try {
        await deleteRender(renderId);
        // NO onRefetch needed - Store handles render deletion via mutation
      } catch (_error) {
        setError('Failed to delete render');
      }
    },
    [concept, deleteRender, setError]
  );

  const handleNoteUpdate = useCallback(
    (note: string) => {
      if (!concept || isArchived) return;

      updateConceptMutation.mutate({
        projectId: project.id,
        conceptId: concept.id,
        updates: {
          title: concept.attributes.title || '',
          note,
        },
      });
    },
    [concept, isArchived, updateConceptMutation, project.id]
  );

  return {
    createConceptFromAngle,
    handleAngleUploadSuccess,
    handleDeleteAngle,
    handleDeleteRender,
    handleNoteUpdate,
    // Cross-concept view-delete conflict dialog wiring.
    deleteConflict,
    deleteConflictDetail,
    isDeletingAngle: deleteAngleMutation.isPending,
    handleDeleteConflictChoose,
    dismissDeleteConflict,
  };
}
